import { getTokenMutation } from '@components/alfakher/pylot-auth-manager/src/graphql/getTokenMutation'
import { getTokenUsingSocialMutation } from '@components/alfakher/pylot-auth-manager/src/graphql/getTokenUsingSocialMutation'

import { graphqlFetch } from '@pylot-data/graphqlFetch'
import type { CustomerToken } from '@pylot-data/fwrdschema'
import { useCart, mergeCarts } from '@corratech/pylot-cart-manager'
import { useAccount } from '@corratech/pylot-auth-manager/src/use-account'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useAuthUI } from '@corratech/pylot-auth-manager/src/AuthContext'
import { SocialLoginType } from '@components/SocialLogin/SocialLoginScript'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

export type GenerateTokenResponse = {
    data: CustomerToken
}

export type LoginArguments = {
    email: string
    password: string
    cartId?: string
}

export type UseLoginType = {
    login: (
        params: LoginArguments
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) => Promise<any> | { reset_password?: boolean }
    loginUsingToken: (
        token: string
    ) => Promise<any> | { reset_password?: boolean }
    socialLogin: (
        token: string,
        socialLoginType: SocialLoginType
    ) => Promise<any> | { reset_password?: boolean }
    isLoading?: boolean
}

export const useLogin = (): UseLoginType => {
    const { mutate: fetchAccountInfo } = useAccount()
    const { mutate: updateCart } = useCart()
    const { locale } = useRouter()
    const { loginCallbacks, clearAuthCallback } = useAuthUI()
    const { setSocialLoader, isSocialLoading } = useAlfakherContext()
    const [isLoading, setIsLoading] = useState(false)

    return {
        login: async (params: LoginArguments) => {
            setIsLoading(true)
            const res = await graphqlFetch<
                LoginArguments,
                { generateCustomerToken: CustomerToken }
            >({
                query: getTokenMutation,
                variables: params,
                locale
            })

            // Prompt the customer to reset password if we need to "migrate" the customer to the new site
            if (res.data?.generateCustomerToken?.reset_password) {
                setIsLoading(false)

                return {
                    reset_password: true
                }
            }

            if (res.errors) {
                setIsLoading(false)
                throw res.errors
            }

            localStorage.setItem(
                'pylot_token',
                res.data.generateCustomerToken.token!
            )
            localStorage.removeItem('is_authorised')
            localStorage.setItem('pylot_login_time', Date.now().toString())

            const guestCartId = localStorage.getItem('cart_id')
            const result = await Promise.all([
                fetchAccountInfo(),
                guestCartId
                    ? mergeCarts({ guestCartId, locale }).then((response) =>
                          updateCart(response, false)
                      )
                    : (Promise.resolve() as Promise<any>)
            ])
            setIsLoading(false)
            //handle login callbacks
            if (loginCallbacks.length) {
                loginCallbacks.forEach((callback) => {
                    if (typeof callback === 'function') {
                        callback()
                    }
                })
                clearAuthCallback('LOGIN_VIEW')
            }

            return result
        },
        socialLogin: async (token: string, loginType: SocialLoginType) => {
            setIsLoading(true)
            setSocialLoader(true)
            const res = await graphqlFetch<
                { token: string; loginType: SocialLoginType },
                { generateCustomerTokenBySocialLogin: CustomerToken }
            >({
                query: getTokenUsingSocialMutation,
                variables: { token, loginType },
                locale
            })

            if (res.errors) {
                setIsLoading(false)
                setSocialLoader(false)
                throw res.errors
            }

            localStorage.setItem(
                'pylot_token',
                res.data.generateCustomerTokenBySocialLogin.token!
            )
            localStorage.removeItem('is_authorised')
            localStorage.setItem('pylot_login_time', Date.now().toString())

            const guestCartId = localStorage.getItem('cart_id')
            const result = await Promise.all([
                fetchAccountInfo(),
                guestCartId
                    ? mergeCarts({ guestCartId, locale }).then((response) =>
                          updateCart(response, false)
                      )
                    : (Promise.resolve() as Promise<any>)
            ])
            setIsLoading(false)
            setSocialLoader(false)
            //handle login callbacks
            if (loginCallbacks.length) {
                loginCallbacks.forEach((callback) => {
                    if (typeof callback === 'function') {
                        callback()
                    }
                })
                clearAuthCallback('LOGIN_VIEW')
            }

            return result
        },
        loginUsingToken: async (token: string) => {
            setIsLoading(true)
            localStorage.setItem('pylot_token', token)
            localStorage.removeItem('is_authorised')
            localStorage.setItem('pylot_login_time', Date.now().toString())

            const result = await fetchAccountInfo()
            setIsLoading(false)
            //handle login callbacks
            if (loginCallbacks.length) {
                loginCallbacks.forEach((callback) => {
                    if (typeof callback === 'function') {
                        callback()
                    }
                })
                clearAuthCallback('LOGIN_VIEW')
            }

            return result
        },
        isLoading: isSocialLoading || isLoading
    }
}
